import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { DragDropContext } from '@hello-pangea/dnd';
import confetti from 'canvas-confetti';
import { MagnifyingGlass, Trash, X, UserCircle, UserPlus, ChatCircle } from '@phosphor-icons/react';
import api from '../lib/api';
import KanbanColumn from '../components/KanbanColumn';
import TicketDetail from '../components/TicketDetail';
import ProjectSettings from '../components/ProjectSettings';
import TrashPanel from '../components/TrashPanel';
import ProjectChat from '../components/ProjectChat';
import ProjectSwitcher from '../components/ProjectSwitcher';
import UserMenu from '../components/UserMenu';
import LabelFilter from '../components/LabelFilter';
import { toast } from 'sonner';
import { useRealtime } from '../context/RealtimeContext';
import { useAuth } from '../context/AuthContext';
import { extractErrorMessage } from '../lib/errors';
import { useT } from '../i18n/I18nContext';
import { canUseAi } from '../lib/agent';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';

const COLUMNS = ['todo', 'in_progress', 'blocked', 'testing', 'review', 'done'];

const COLUMN_STATUSES = Object.fromEntries(COLUMNS.map((c) => [c, [c]]));

export default function ProjectBoard({ projects, onProjectsChange, onNewProject }) {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLabels, setActiveLabels] = useState(() => new Set());
  const [userFilterMode, setUserFilterMode] = useState('assignee'); // 'assignee' | 'creator'
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' | 'unassigned' | userId
  const [userFilterSearch, setUserFilterSearch] = useState('');
  const [landedInReview, setLandedInReview] = useState(null); // ticket id that just landed
  const [reviewCountBumpKey, setReviewCountBumpKey] = useState(0); // increments to re-trigger badge pop
  const [trashOpen, setTrashOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const boardRef = useRef(null);
  const t = useT();
  const { user } = useAuth();

  const heartShapeRef = useRef(null);
  const fireReviewConfetti = useCallback(() => {
    if (!heartShapeRef.current && typeof confetti.shapeFromPath === 'function') {
      heartShapeRef.current = confetti.shapeFromPath({
        path: 'M167 72c19,-38 37,-56 75,-56 42,0 76,33 76,75 0,76 -76,151 -151,227 -76,-76 -151,-151 -151,-227 0,-42 33,-75 75,-75 38,0 57,18 76,56z',
      });
    }
    const colors = [
      '#0055ff', // brand blue (accent / agent column)
      '#c85d42', // terracotta-500
      '#e4886f', // terracotta-400
      '#f8d5ca', // terracotta-200
      '#f59e0b', // amber-500 (review)
      '#10b981', // emerald-500 (done)
      '#a855f7', // purple-500 (in_progress)
      '#ef4444', // red-500
    ];
    const shapes = heartShapeRef.current ? [heartShapeRef.current] : undefined;
    const rect = boardRef.current?.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const yTop = rect ? rect.top / vh : 0.1;
    const yBottom = rect ? rect.bottom / vh : 0.9;
    const xLeft = rect ? Math.max(rect.left / vw, 0.02) : 0.05;
    const xRight = rect ? Math.min(rect.right / vw, 0.98) : 0.95;
    const common = {
      ticks: 220,
      gravity: 0.75,
      scalar: 1.6,
      colors,
      shapes,
      disableForReducedMotion: true,
    };
    // Left cannon, angled up-right
    confetti({
      ...common,
      particleCount: 60,
      angle: 60,
      spread: 70,
      startVelocity: 55,
      origin: { x: xLeft, y: yBottom - 0.02 },
    });
    // Right cannon, angled up-left
    confetti({
      ...common,
      particleCount: 60,
      angle: 120,
      spread: 70,
      startVelocity: 55,
      origin: { x: xRight, y: yBottom - 0.02 },
    });
    // Top sprinkle drifting down across the board
    confetti({
      ...common,
      particleCount: 40,
      angle: 270,
      spread: 180,
      startVelocity: 20,
      gravity: 1.0,
      origin: { x: 0.5, y: Math.max(yTop, 0) },
    });
  }, []);

  const { onAny } = useRealtime();

  const load = useCallback(async () => {
    try {
      const [p, t, u] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/tickets`),
        api.get('/users'),
      ]);
      setProject(p.data);
      setTickets(t.data);
      setUsers(u.data);
    } catch (err) {
      toast.error(t('projectBoard.loadFailedToast'));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Realtime + polling: clone status updates (WebSocket project_updated, or poll while cloning)
  useEffect(() => {
    const unsub = onAny((msg) => {
      if (msg.type === 'ticket_updated' && msg.payload?.project_id === projectId) {
        // ``ticket_updated`` carries the full ticket including ``deleted`` — a
        // restore flips it from True to False, a soft-delete flips it the other
        // way. Mirror that into the board state.
        if (msg.payload.deleted) {
          setTickets((prev) => prev.filter((t) => t.id !== msg.payload.id));
        } else {
          setTickets((prev) => {
            const idx = prev.findIndex((t) => t.id === msg.payload.id);
            if (idx === -1) return [...prev, msg.payload];
            // The backend shifts the destination column down when a ticket
            // enters it from elsewhere — mirror that so it shows on top here too.
            const enteredColumn = prev[idx].status !== msg.payload.status;
            const next = prev.map((t) => (
              enteredColumn && t.status === msg.payload.status ? { ...t, order: (t.order ?? 0) + 1 } : t
            ));
            next[idx] = msg.payload;
            return next;
          });
        }
      }
      if (
        (msg.type === 'ticket_trashed' || msg.type === 'ticket_purged')
        && msg.payload?.project_id === projectId
      ) {
        setTickets((prev) => prev.filter((t) => t.id !== msg.payload.id));
      }
      if (msg.type === 'project_updated' && msg.project_id === projectId && msg.payload) {
        setProject(msg.payload);
      }
    });
    return unsub;
  }, [onAny, projectId]);

  const anyCloning = (project?.repos || []).some((r) => r.clone_status === 'cloning');
  useEffect(() => {
    if (!projectId || !anyCloning) return undefined;
    const id = setInterval(() => {
      api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
    }, 2500);
    return () => clearInterval(id);
  }, [projectId, anyCloning]);

  // Most-used first: the header shows only the top few, the rest go in a menu.
  const allLabels = useMemo(() => {
    const counts = new Map();
    tickets.forEach((tk) => (tk.labels || []).forEach((l) => counts.set(l, (counts.get(l) || 0) + 1)));
    return Array.from(counts, ([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [tickets]);

  const deleteLabel = async (label) => {
    try {
      await api.delete(`/projects/${projectId}/labels/${encodeURIComponent(label)}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('projectBoard.labelDeleteFailedToast')));
      throw err;
    }
    setTickets((prev) => prev.map((tk) => (
      tk.labels?.includes(label) ? { ...tk, labels: tk.labels.filter((l) => l !== label) } : tk
    )));
    setActiveLabels((prev) => {
      if (!prev.has(label)) return prev;
      const next = new Set(prev);
      next.delete(label);
      return next;
    });
    toast.success(t('projectBoard.labelDeletedToast', { name: label }));
  };

  // ``users`` is the global list — kept around for name/initial lookups so cards
  // can render the avatar of someone who was assigned before being removed from
  // the project. ``projectMembers`` is the narrowed list used for every UI that
  // *selects* an assignee (filter, create dialog, card menu, detail pane).
  const projectMembers = useMemo(() => {
    const ids = new Set(project?.member_ids || []);
    return users.filter((u) => ids.has(u.id));
  }, [users, project?.member_ids]);

  const filteredProjectMembers = useMemo(() => {
    const q = userFilterSearch.trim().toLowerCase();
    if (!q) return projectMembers;
    return projectMembers.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [projectMembers, userFilterSearch]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  // Ticket-number queries: "42", "#42", "T42", "T#42", "t-42".
  const numberMatch = normalizedQuery.match(/^t?\s*[#-]?\s*(\d+)$/);
  const queryNumber = numberMatch ? Number(numberMatch[1]) : null;
  const hasFilters =
    normalizedQuery.length > 0 || activeLabels.size > 0 || assigneeFilter !== 'all';

  const matchesFilters = useCallback(
    (tk) => {
      if (normalizedQuery) {
        const title = (tk.title || '').toLowerCase();
        const desc = (tk.description || '').toLowerCase();
        const numberHit = queryNumber !== null && tk.number === queryNumber;
        if (!numberHit && !title.includes(normalizedQuery) && !desc.includes(normalizedQuery)) return false;
      }
      if (activeLabels.size > 0) {
        const labels = tk.labels || [];
        for (const l of activeLabels) if (!labels.includes(l)) return false;
      }
      if (assigneeFilter === 'unassigned') {
        if (userFilterMode === 'assignee' && tk.assignee_id) return false;
      } else if (assigneeFilter !== 'all') {
        const field = userFilterMode === 'creator' ? tk.creator_id : tk.assignee_id;
        if (field !== assigneeFilter) return false;
      }
      return true;
    },
    [normalizedQuery, queryNumber, activeLabels, assigneeFilter, userFilterMode],
  );

  const filteredTickets = useMemo(
    () => tickets.filter(matchesFilters),
    [tickets, matchesFilters],
  );

  const ticketsByCol = Object.fromEntries(COLUMNS.map((col) => [
    col,
    filteredTickets.filter((t) => t.status === col).sort((a, b) => a.order - b.order),
  ]));

  const toggleLabel = (label) => {
    setActiveLabels((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveLabels(new Set());
    setAssigneeFilter('all');
  };

  const assigneeLabel = (() => {
    if (assigneeFilter === 'all') {
      return userFilterMode === 'creator'
        ? t('projectBoard.allCreators')
        : t('projectBoard.allAssignees');
    }
    if (assigneeFilter === 'unassigned') return t('projectBoard.unassigned');
    // Look up in the full list so a filter pinned to a now-removed member still
    // renders that member's name instead of going blank.
    const u = users.find((x) => x.id === assigneeFilter);
    if (u?.name) return u.name;
    return userFilterMode === 'creator'
      ? t('projectBoard.filterByCreator')
      : t('projectBoard.filterByAssignee');
  })();

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newStatus = destination.droppableId;
    const statusesInDest = COLUMN_STATUSES[newStatus] || [newStatus];
    // Optimistic update
    const moving = tickets.find((t) => t.id === draggableId);
    if (!moving) return;

    const newTickets = tickets.filter((t) => t.id !== draggableId);
    const destList = newTickets
      .filter((t) => statusesInDest.includes(t.status))
      .sort((a, b) => a.order - b.order);
    // A ticket entering another column lands on top; reorders within a column keep the drop spot.
    const changedColumn = source.droppableId !== destination.droppableId;
    const destIndex = changedColumn ? 0 : destination.index;
    destList.splice(destIndex, 0, { ...moving, status: newStatus });
    destList.forEach((t, i) => { t.order = i; });
    const others = newTickets.filter((t) => !statusesInDest.includes(t.status));
    setTickets([...others, ...destList]);

    if (newStatus === 'review' && source.droppableId !== 'review') {
      fireReviewConfetti();
      setLandedInReview(draggableId);
      setReviewCountBumpKey((k) => k + 1);
      setTimeout(() => setLandedInReview((cur) => (cur === draggableId ? null : cur)), 600);
    }

    try {
      await api.post(`/tickets/${draggableId}/move`, { status: newStatus, order: destIndex });
    } catch (err) {
      toast.error(t('projectBoard.moveFailedToast'));
      load();
    }
  };

  const createTicket = async (payload) => {
    try {
      const { data } = await api.post('/tickets', { ...payload, project_id: projectId });
      setTickets((prev) => {
        // The backend broadcasts ``ticket_updated`` before the POST resolves, so
        // the realtime handler may have already appended this ticket — drop it
        // before re-adding so it doesn't show up twice.
        const bumped = prev
          .filter((tk) => tk.id !== data.id)
          .map((tk) => (tk.status === 'todo' ? { ...tk, order: (tk.order ?? 0) + 1 } : tk));
        return [...bumped, data];
      });
      toast.success(t('projectBoard.ticketCreatedToast'));
      onProjectsChange?.();
    } catch (err) {
      toast.error(t('projectBoard.ticketCreateFailedToast'));
    }
  };

  const deleteTicket = async (ticketId) => {
    try {
      await api.delete(`/tickets/${ticketId}`);
      setTickets((prev) => prev.filter((tk) => tk.id !== ticketId));
      toast.success(t('projectBoard.ticketDeletedToast'));
      queueMicrotask(() => {
        setSelected((s) => (s?.id === ticketId ? null : s));
        load();
      });
      onProjectsChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projectBoard.ticketDeleteFailedToast'));
      throw err;
    }
  };

  const updateTicketAssignee = async (ticketId, assigneeId) => {
    try {
      const { data } = await api.patch(`/tickets/${ticketId}`, { assignee_id: assigneeId });
      setTickets((prev) => prev.map((tk) => (tk.id === ticketId ? data : tk)));
      setSelected((s) => (s?.id === ticketId ? { ...s, ...data } : s));
    } catch (err) {
      toast.error(extractErrorMessage(err, t('projectBoard.assigneeUpdateFailedToast')));
      load();
    }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center text-zinc-500">{t('common.loading')}</div>;
  if (!project) {
    // Gone or no access → "/" picks another project. A project that is still
    // listed just failed to load; retry here instead of bouncing back to it.
    if (!projects.some((p) => p.id === projectId)) return <Navigate to="/" replace />;
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-zinc-500" data-testid="project-load-failed">
        {t('projectBoard.loadFailedToast')}
        <button
          type="button"
          onClick={() => { setLoading(true); load(); }}
          className="text-xs font-semibold text-zinc-900 px-3 py-1.5 rounded-sm border border-zinc-200 hover:border-zinc-400"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  const repos = project.repos || [];
  const anyRepoCloning = repos.some((r) => r.clone_status === 'cloning');
  const failedRepos = repos.filter((r) => r.clone_status === 'failed');
  // Lazy clone: ``cloning`` only happens during an agent run (or an explicit
  // re-clone). The board stays usable — the user can keep editing tickets while
  // the agent fetches the repo in the background.
  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden h-full" data-testid="project-board">
      <header className="border-b border-zinc-200 px-4 md:px-8 py-3 bg-white flex flex-wrap items-center gap-3 md:gap-4 flex-shrink-0">
        <div className="flex-shrink-0 min-w-0">
          <ProjectSwitcher projects={projects} currentProject={project} onNewProject={onNewProject} />
        </div>

        <div
          data-testid="board-filter-bar"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1"
        >
          <div className="relative flex items-center">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-2.5 text-zinc-400 pointer-events-none" />
            <input
              type="text"
              data-testid="board-search-input"
              placeholder={t('projectBoard.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 w-56 pl-7 pr-7 text-xs rounded-sm border border-zinc-200 bg-white focus:outline-none focus:border-zinc-400"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 p-0.5 text-zinc-400 hover:text-zinc-700 rounded-sm"
                aria-label={t('projectBoard.clearFilters')}
              >
                <X size={11} weight="bold" />
              </button>
            )}
          </div>

          <div
            data-testid="board-user-filter"
            className="inline-flex items-center h-8 rounded-sm border border-zinc-200 bg-white overflow-hidden"
          >
            <div className="flex items-center gap-1.5 px-2 border-r border-zinc-200">
              <span
                className={`text-[10px] whitespace-nowrap transition-colors ${
                  userFilterMode === 'assignee' ? 'text-zinc-900 font-medium' : 'text-zinc-400'
                }`}
              >
                {t('projectBoard.filterModeAssignee')}
              </span>
              <Switch
                data-testid="board-user-filter-mode-switch"
                checked={userFilterMode === 'creator'}
                onCheckedChange={(checked) => {
                  setUserFilterMode(checked ? 'creator' : 'assignee');
                  if (checked && assigneeFilter === 'unassigned') setAssigneeFilter('all');
                }}
                className="data-[state=checked]:bg-zinc-900 data-[state=unchecked]:bg-zinc-300"
              />
              <span
                className={`text-[10px] whitespace-nowrap transition-colors ${
                  userFilterMode === 'creator' ? 'text-zinc-900 font-medium' : 'text-zinc-400'
                }`}
              >
                {t('projectBoard.filterModeCreator')}
              </span>
            </div>

          <DropdownMenu onOpenChange={(open) => { if (!open) setUserFilterSearch(''); }}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="board-assignee-filter"
                className={`h-8 inline-flex items-center gap-1.5 px-2.5 text-xs transition-colors ${
                  assigneeFilter !== 'all'
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-700 hover:bg-zinc-50'
                }`}
              >
                {userFilterMode === 'creator' ? (
                  <UserPlus size={13} weight={assigneeFilter !== 'all' ? 'fill' : 'bold'} />
                ) : (
                  <UserCircle size={13} weight={assigneeFilter !== 'all' ? 'fill' : 'bold'} />
                )}
                <span className="truncate max-w-[120px]">{assigneeLabel}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="z-[100] w-56 rounded-sm p-0">
              <div
                className="p-2 border-b border-zinc-100"
                onPointerDown={(e) => e.preventDefault()}
              >
                <div className="relative flex items-center">
                  <MagnifyingGlass size={12} weight="bold" className="absolute left-2 text-zinc-400 pointer-events-none" />
                  <input
                    type="text"
                    data-testid="board-user-filter-search"
                    value={userFilterSearch}
                    onChange={(e) => setUserFilterSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder={t('projectBoard.userFilterSearchPlaceholder')}
                    className="h-7 w-full pl-7 pr-2 text-xs rounded-sm border border-zinc-200 bg-white focus:outline-none focus:border-zinc-400"
                    autoFocus
                  />
                </div>
              </div>
              <div className="p-1">
              <DropdownMenuRadioGroup value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <DropdownMenuRadioItem value="all" className="rounded-sm text-xs">
                  {userFilterMode === 'creator'
                    ? t('projectBoard.allCreators')
                    : t('projectBoard.allAssignees')}
                </DropdownMenuRadioItem>
                {userFilterMode === 'assignee' && (
                <DropdownMenuRadioItem value="unassigned" className="rounded-sm text-xs">
                  {t('projectBoard.unassigned')}
                </DropdownMenuRadioItem>
                )}
                {filteredProjectMembers.map((u) => {
                  const initials = (u.name || u.email || '?')
                    .split(/\s+/)
                    .map((p) => p[0])
                    .filter(Boolean)
                    .join('')
                    .slice(0, 2)
                    .toUpperCase();
                  return (
                    <DropdownMenuRadioItem key={u.id} value={u.id} className="rounded-sm text-xs">
                      <span className="flex items-center gap-2 min-w-0">
                        <span
                          style={u.color ? { backgroundColor: u.color } : undefined}
                          className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-semibold text-white ${
                            u.color ? '' : 'bg-zinc-500'
                          }`}
                        >
                          {initials}
                        </span>
                        <span className="truncate">{u.name}</span>
                      </span>
                    </DropdownMenuRadioItem>
                  );
                })}
                {filteredProjectMembers.length === 0 && userFilterSearch.trim() && (
                  <p className="px-2 py-1.5 text-xs text-zinc-500">{t('projectBoard.noUsersMatch')}</p>
                )}
              </DropdownMenuRadioGroup>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>

          {allLabels.length > 0 && (
            <LabelFilter
              labels={allLabels}
              activeLabels={activeLabels}
              onToggle={toggleLabel}
              onDelete={deleteLabel}
            />
          )}

          {hasFilters && (
            <button
              type="button"
              data-testid="board-clear-filters"
              onClick={clearFilters}
              className="h-8 inline-flex items-center gap-1 px-2 text-[11px] text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm flex-shrink-0"
            >
              <X size={11} weight="bold" />
              {t('projectBoard.clearFilters')}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            data-testid="project-chat-btn"
            onClick={() => setChatOpen(true)}
            title={t('chat.title') || 'Project chat'}
            aria-label={t('chat.title') || 'Project chat'}
            className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-sm border border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50"
          >
            <ChatCircle size={12} weight="bold" />
            <span className="uppercase tracking-wider text-[10px]">
              {t('chat.btn') || 'Chat'}
            </span>
          </button>
          <ProjectSettings project={project} users={users} onProjectChange={setProject} onProjectsChange={onProjectsChange} />
          <button
            type="button"
            data-testid="project-trash-btn"
            onClick={() => setTrashOpen(true)}
            title={t('projectBoard.trashTitle')}
            aria-label={t('projectBoard.trashTitle')}
            className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-sm border border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50"
          >
            <Trash size={12} weight="bold" />
            <span className="uppercase tracking-wider text-[10px]">
              {t('projectBoard.trashBtn')}
            </span>
          </button>
          <UserMenu />
        </div>
      </header>

      {repos.length === 0 && (
        <div
          data-testid="no-repos-banner"
          className="px-4 md:px-8 py-3 bg-amber-50 border-b border-amber-100 text-sm text-amber-950"
        >
          <strong className="font-semibold">{t('projectBoard.noReposTitle')}</strong>
          <span className="text-amber-800"> — {t('projectBoard.noReposBody')}</span>
        </div>
      )}
      {anyRepoCloning && (
        <div data-testid="clone-status-banner" className="px-4 md:px-8 py-3 bg-amber-50 border-b border-amber-100 text-sm text-amber-950">
          <strong className="font-semibold">{t('projectBoard.cloningTitle')}</strong>
          <span className="text-amber-800"> — </span>
          <span className="text-amber-800">
            {t('projectBoard.cloningBodyMulti', {
              names: repos.filter((r) => r.clone_status === 'cloning').map((r) => r.name).join(', '),
            })}
          </span>
        </div>
      )}
      {failedRepos.length > 0 && (
        <div data-testid="clone-error-banner" className="px-4 md:px-8 py-3 bg-red-50 border-b border-red-100 text-sm text-red-950 space-y-1">
          <div>
            <strong className="font-semibold">{t('projectBoard.cloneFailedTitle')}</strong>
            <span className="text-red-900">
              {t('projectBoard.cloneFailedBody', {
                api: 'api',
                writeRepo: 'write_repository',
              })}
            </span>
            <strong>{t('projectBoard.cloneFailedAction')}</strong>.
          </div>
          <ul className="text-xs text-red-800 font-mono space-y-0.5">
            {failedRepos.map((r) => (
              <li key={r.id} data-testid={`clone-error-repo-${r.id}`}>
                <strong>{r.name}</strong>: {r.clone_error || t('common.unknown')}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div ref={boardRef} className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-3 p-4 md:p-6 items-stretch h-full min-h-0 min-w-full">
            {COLUMNS.map((status) => (
              <KanbanColumn
                key={status}
                status={status}
                tickets={ticketsByCol[status]}
                users={users}
                projectMembers={projectMembers}
                projectId={projectId}
                project={project}
                onCreate={createTicket}
                onTicketClick={(t) => setSelected(t)}
                onTicketDelete={deleteTicket}
                onTicketAssigneeChange={updateTicketAssignee}
                landedTicketId={status === 'review' ? landedInReview : null}
                countBumpKey={status === 'review' ? reviewCountBumpKey : 0}
              />
            ))}
          </div>
        </DragDropContext>
      </div>

      {selected && (
        <TicketDetail
          ticket={selected}
          users={users}
          projectMembers={projectMembers}
          project={project}
          onClose={() => setSelected(null)}
          onChange={load}
          onDeleteTicket={deleteTicket}
        />
      )}

      <TrashPanel
        open={trashOpen}
        onOpenChange={setTrashOpen}
        projectId={projectId}
        onChange={() => { load(); onProjectsChange?.(); }}
      />

      {chatOpen && (
        <ProjectChat
          projectId={projectId}
          users={users}
          currentUserId={user?.id}
          canUseAi={canUseAi(project, user?.id)}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}
